import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  accountQuery,
  buildCasinoGameFilters,
  casinoGamesQuery,
  useAuthSession,
} from '@/api';
import liveCasinoFeaturedArt from '@/assets/home/live-casino-featured.png';
import { cn } from '@/lib/cn';
import { GameTile } from '@/ui/casino/GameTile';
import { useCasinoFavorites } from '@/ui/casino/useCasinoFavorites';
import { HorizontalScrollRow, Icon, Spinner } from '@/ui/common';

export interface HomeLiveCasinoProps {
  className?: string;
}

/** Figma Live Casino featured promo — static art linking to live casino. */
function LiveCasinoFeatured({ className }: { className?: string }) {
  return (
    <Link
      to="/livecasino"
      className={cn(
        'relative block h-[150px] w-full shrink-0 overflow-hidden rounded-md bg-bg-canvas lg:h-[240px] lg:w-[312px]',
        className,
      )}
      aria-label="Live Casino"
    >
      <img
        src={liveCasinoFeaturedArt}
        alt=""
        className="absolute inset-0 size-full object-cover"
        draggable={false}
      />
    </Link>
  );
}

/**
 * Figma CasinoGameSection Tone=Live (Desktop `1827:5041`, Mobile `1827:5069`).
 * Featured promo beside (desktop) or above (mobile) a gold-border live game rail.
 */
export function HomeLiveCasino({ className }: HomeLiveCasinoProps) {
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const { showFavorite, toggleFavorite } = useCasinoFavorites();

  const filters = useMemo(
    () => buildCasinoGameFilters({ account, user, liveMode: true }),
    [account, user],
  );
  const gamesQuery = useQuery(casinoGamesQuery(filters));
  const games = gamesQuery.data?.Data ?? [];
  const isLoading = gamesQuery.isLoading;

  if (!isLoading && games.length === 0) return null;

  return (
    <section
      className={cn('flex w-full flex-col rounded-md', className)}
      aria-label="Live Casino"
    >
      <div className="flex flex-col gap-4 overflow-hidden rounded-md bg-bg-canvas p-3 lg:p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <Icon name="livecasino" size={24} className="shrink-0 text-status-warning" />
            <h2 className="truncate text-base font-bold italic leading-6 text-text-primary lg:text-xl lg:leading-6">
              Live Casino
            </h2>
          </div>
          <Link
            to="/livecasino"
            className="shrink-0 rounded-full bg-status-warning px-3 py-1.5 text-xs font-bold text-bg-elevated"
          >
            See all &gt;
          </Link>
        </div>

        {/* Mobile: featured above rail (Figma Size=Mobile Tone=Live) */}
        <div className="flex flex-col gap-3 lg:hidden">
          <LiveCasinoFeatured />
          {isLoading && games.length === 0 ? (
            <div className="flex h-36 items-center justify-center">
              <Spinner label="Loading live casino" />
            </div>
          ) : (
            <HorizontalScrollRow
              aria-label="Live casino games"
              className="gap-3 pl-0"
            >
              {games.map((game) => (
                <GameTile
                  key={`${game.ProviderGameId}-${game.GameTag}`}
                  game={game}
                  categoryOverride="livecasino"
                  showFavorite={showFavorite}
                  onToggleFavorite={toggleFavorite}
                  tone="live"
                  size="sm"
                  className="h-[144px] w-[108px] shrink-0"
                />
              ))}
            </HorizontalScrollRow>
          )}
        </div>

        {/* Desktop: featured beside rail (Figma Size=Desktop Tone=Live) */}
        <div className="hidden gap-4 lg:flex lg:items-start">
          <LiveCasinoFeatured />
          <div className="min-w-0 flex-1">
            {isLoading && games.length === 0 ? (
              <div className="flex h-60 items-center justify-center">
                <Spinner label="Loading live casino" />
              </div>
            ) : (
              <HorizontalScrollRow
                aria-label="Live casino games"
                withDesktopArrows
                className="gap-4 pl-0"
              >
                {games.map((game) => (
                  <GameTile
                    key={`${game.ProviderGameId}-${game.GameTag}`}
                    game={game}
                    categoryOverride="livecasino"
                    showFavorite={showFavorite}
                    onToggleFavorite={toggleFavorite}
                    tone="live"
                    className="h-[240px] w-[180px] shrink-0"
                  />
                ))}
              </HorizontalScrollRow>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
